/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useMemo } from 'react';
import { css } from 'aphrodite';
import { mixins } from '@blend/consumer-components';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { ConsumerAppConfigContext, useConsumerTheme } from '../../../../hooks/useConsumerTheme';
import { mediaBreakpoints, mediaOrientations } from '../../../../common/responsive';
import { MemoizedParticipant } from '../../Participant/Participant';
import { zIndex } from '../../../../style/zIndex';
import { useParticipantTracks } from '../hooks/useParticipantTracks';
import { useDominantSpeakerParticipant } from '../hooks/useDominantSpeakerParticipant';

const generateStyles = (appConfig: ConsumerAppConfigContext) => ({
  stripWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    ...mixins.paddingVertical(8),
    backgroundColor: appConfig.colors.blackPearl,
    [mediaBreakpoints.desktop]: {
      height: 156,
    },
    [mediaBreakpoints.mobile]: {
      height: '13%',
      ...mixins.paddingHorizontal(8),
      zIndex: zIndex.mobileParticipantStrip,
      [mediaOrientations.landscape]: {
        height: '100%',
        width: '25%',
        flexDirection: 'column',
      },
    },
    participantWrapper: {
      height: '100%',
      ':not(:last-child)': {
        ...mixins.margin(0, 4, 0, 0),
      },
    },
  },
});
export namespace TwilioParticipantStrip {
  export interface Props {
    isParticipantsTabView?: boolean;
  }
}

const TwilioParticipantStrip: React.FC<TwilioParticipantStrip.Props> = ({}) => {
  const { users, isMobileView } = useRootSelector(state => ({
    users: state.signingRoom.users,
    isMobileView: state.layout.isMobileView,
  }));
  const { styles } = useConsumerTheme(generateStyles);
  const { isLoading, pinnedParticipants, nonPinnedParticipants } = useParticipantTracks();
  const { dominantSpeakerParticipant } = useDominantSpeakerParticipant(nonPinnedParticipants);

  const participantsProps = useMemo(() => {
    // Local participant and notary participant will always be visible
    const pinnedParticipantsProps = pinnedParticipants.map(participant => ({
      participant,
      pinned: true,
      dataIt: 'Pinned-Participant',
    }));

    // dominantSpeakerParticipant is null if there are no non-pinned participants in the room
    const dominantSpeakerParticipants =
      isMobileView && !!dominantSpeakerParticipant ? [dominantSpeakerParticipant] : [];
    //  Show all participant on desktop, but only show one non-pinned dominant speaker participant on mobile
    const nonPinnedParticipantsProps = (isMobileView ? dominantSpeakerParticipants : nonPinnedParticipants).map(
      participant => ({
        participant,
        pinned: false,
        dataIt: isMobileView ? 'Dominant-Speaker-Participant' : 'Nonpinned-Participant',
      }),
    );
    return [...pinnedParticipantsProps, ...nonPinnedParticipantsProps];
  }, [pinnedParticipants, dominantSpeakerParticipant, nonPinnedParticipants, isMobileView]);

  if (isLoading) {
    return null;
  }

  return (
    <div className={css(styles.stripWrapper)} data-it="ParticipantStrip">
      {participantsProps.map(participantProps => (
        <MemoizedParticipant
          key={participantProps.participant.identity}
          participant={participantProps.participant}
          users={users}
          pinned={participantProps.pinned}
          data-it={participantProps.dataIt}
        />
      ))}
    </div>
  );
};

export const MemoizedTwilioParticipantStrip = React.memo(TwilioParticipantStrip);
